import { useEffect, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AnimatePresence, motion } from 'motion/react';
import { CloseIcon } from '@wbk/svg';
import { Button, ImageWithFallback, ImageWithPlaceholder, Paper, Typography } from '@wbk/ui';
import { type WebSection, usePage } from '@wbk/contentful/api';
import { useScrollLock } from '@/hooks/use-scroll-lock';
import useLocalization from '@/context/localization/useLocalization';

type Props = {
  open: boolean;
  onOpenChange: () => void;
};

export default function ContactModal({ open, onOpenChange }: Props) {
  const { locale } = useLocalization();
  const { t } = useTranslation('common');
  const { lock, unlock } = useScrollLock({
    autoLock: false,
  });

  const { data: contactPage } = usePage({
    lang: locale,
    limit: 1,
    where: { slug: 'rs-contact-us' },
  });

  useEffect(() => {
    if (open) {
      lock();
    } else {
      unlock();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const sections = (contactPage?.sectionsCollection?.items || []).filter(
    (s): s is { __typename: 'WebSection' } & WebSection => s?.__typename === 'WebSection'
  ) as WebSection[];

  const inquiriesSection = sections.find((s) => s?.renderType === 'anchors');

  const followSection = sections.find((s) => s?.renderType === 'link-card');

  const inquiriesLinks = inquiriesSection?.webLinksCollection?.items || [];

  const normalizeHref = (href?: string, opts: { tel?: boolean } = {}) => {
    if (!href) return '';
    if (opts.tel) return href.startsWith('tel:') ? href : `tel:${href}`;
    if (href.startsWith('http')) return href;
    return `https://${href}`;
  };

  // Find ehtemam link by id
  const ehtemamLink = inquiriesLinks.find((l) => l?.id?.toLowerCase().includes('ehtemam'));

  // Find phone link by id
  const phoneLink = inquiriesLinks.find((l) => l?.id?.toLowerCase().includes('phone'));

  const phoneNumber = phoneLink?.href?.trim() || '';

  const socialMediaLinks = useMemo(() => {
    const socialLinks = followSection?.webLinksCollection?.items || [];
    return socialLinks
      .map((item) => {
        const imageUrl =
          typeof item?.image === 'string'
            ? item.image
            : (item?.image as { url?: string } | undefined)?.url || '';
        return {
          name: item?.label || item?.id || '',
          url: item?.href || '',
          icon: imageUrl,
        };
      })
      .filter((i) => i.url);
  }, [followSection]);

  const contactTitle = contactPage?.title;
  const inquiriesTitle = inquiriesSection?.title;
  const followTitle = followSection?.title;
  const followSubtitle = followSection?.subtitle;

  const ehtHref = normalizeHref(ehtemamLink?.href);
  const ehtIcon =
    (ehtemamLink?.image as { url?: string } | undefined)?.url ||
    (ehtemamLink?.image as string | undefined);
  const ehtLabel = ehtemamLink?.label;
  const phoneHrefValue = normalizeHref(phoneLink?.href, { tel: true });

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, ease: 'easeInOut' }}
          className='pointer-events-auto fixed inset-0 z-[999]'
        >
          <div className='h-full w-full bg-black/95'>
            {/* Header */}
            <div className='container relative z-20 flex w-full justify-between gap-6 px-4 py-8 md:px-8 2xl:gap-10'>
              <Link to={`/${locale}`}>
                <ImageWithPlaceholder src='/images/logo.svg' alt='Logo' className='w-20 xl:w-28' />
              </Link>

              <div className='px-4'>
                <Button
                  rounded
                  onClick={onOpenChange}
                  theme='transparent'
                  className='border-primary hover:bg-primary h-9 w-9 rounded-full border bg-transparent p-0 transition-all duration-200'
                >
                  <CloseIcon />
                </Button>
              </div>
            </div>

            {/* Main Content */}
            <div className='absolute inset-0 z-10 flex h-full flex-col items-center justify-center gap-10 px-4'>
              <div className='flex w-full max-w-4xl flex-col items-center gap-16'>
                {/* Title */}
                <Typography
                  as='h2'
                  variant='heading-L'
                  strong
                  className='font-gellix rtl:font-expo-arabic text-center uppercase text-white md:text-[80px] md:leading-[72px] md:tracking-[-4px]'
                >
                  {contactTitle}
                </Typography>

                {/* Contact Section */}
                <div className='flex w-full flex-col gap-8'>
                  {inquiriesTitle && (
                    <Typography as='p' variant='body-M' strong className='-mb-5 text-white'>
                      {inquiriesTitle}
                    </Typography>
                  )}
                  {/* For inquiries and complaints */}
                  <div className='flex flex-col gap-6 sm:flex-row sm:gap-6'>
                    {/* Ehtemam Center Card */}
                    <a
                      href={ehtHref}
                      target='_blank'
                      rel='noopener noreferrer'
                      className='group block flex-1'
                    >
                      <Paper className='flex items-center justify-between gap-4 rounded-[14px] border-white/20 p-6 transition-all hover:border-white/30'>
                        <div className='flex items-center gap-3'>
                          <div className='flex h-14 w-14 items-center justify-center rounded-[11.2px] bg-white'>
                            <ImageWithFallback
                              src={ehtIcon}
                              alt={ehtLabel}
                              className='h-7 w-auto'
                            />
                          </div>
                          <Typography
                            as='span'
                            variant='body-M'
                            strong
                            className='capitalize text-white'
                          >
                            {ehtLabel}
                          </Typography>
                        </div>
                        <svg
                          width='28'
                          height='28'
                          viewBox='0 0 24 24'
                          fill='none'
                          xmlns='http://www.w3.org/2000/svg'
                          className='transition-transform group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1'
                        >
                          <path
                            d='M9 18L15 12L9 6'
                            stroke='#A1A1AA'
                            strokeWidth='2'
                            strokeLinecap='round'
                            strokeLinejoin='round'
                          />
                        </svg>
                      </Paper>
                    </a>

                    {/* Phone Number Card */}
                    <a href={phoneHrefValue} className='block flex-1'>
                      <Paper className='flex items-center gap-6 rounded-[14px] border-white/20 p-6 transition-all hover:border-white/30'>
                        <div className='flex h-12 w-12 items-center justify-center rounded-[12px]'>
                          <ImageWithFallback
                            src='/icons/contact/phone.svg'
                            alt='Phone'
                            className='h-full w-full'
                          />
                        </div>
                        <div className='flex flex-col gap-2'>
                          <Typography as='p' variant='body-XS' className='text-white'>
                            {t('privacy.contactInfo.phoneNumber')}
                          </Typography>
                          <Typography as='p' variant='body-M' strong className='text-white'>
                            {phoneNumber}
                          </Typography>
                        </div>
                      </Paper>
                    </a>
                  </div>

                  {/* Social Media Section */}
                  <Paper className='flex flex-col justify-between gap-6 rounded-[14px] border-white/20 p-6 sm:flex-row'>
                    <div className='flex flex-col gap-2'>
                      <Typography as='p' variant='body-XS' className='text-white'>
                        {followTitle}
                      </Typography>
                      <Typography as='p' variant='body-M' strong className='text-white'>
                        {followSubtitle}
                      </Typography>
                    </div>

                    {/* Social Icons */}
                    <div className='flex items-center gap-4'>
                      {socialMediaLinks.map((social) => (
                        <a
                          key={social.name}
                          href={social.url}
                          target='_blank'
                          rel='noopener noreferrer'
                          className='flex h-12 w-12 items-center justify-center rounded-[12px] bg-white/10 transition-all hover:scale-110 hover:bg-white/15'
                          aria-label={social.name}
                        >
                          <ImageWithFallback
                            src={social.icon}
                            alt={social.name}
                            className='h-6 w-6'
                          />
                        </a>
                      ))}
                    </div>
                  </Paper>
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

type ContactButtonProps = React.ButtonHTMLAttributes<HTMLButtonElement>;

export function ContactButton({ onClick, className, ...props }: ContactButtonProps) {
  const { locale } = useLocalization();

  const { data: contactPage } = usePage({
    lang: locale,
    limit: 1,
    where: { slug: 'rs-contact-us' },
  });

  const contactTitle = contactPage?.title;

  return (
    <Button
      phill
      theme='transparent'
      shape='outlined'
      onClick={onClick}
      className={twMerge('border-primary flex-1 rounded-full px-4 py-1.5 text-white', className)}
      {...props}
    >
      <Typography variant='body-XS' strong className='capitalize'>
        {contactTitle}
      </Typography>
    </Button>
  );
}
